import React, { useRef, useState } from 'react';

import { ClickAwayListener } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';

import { TextField, Typography } from '..';
import { TextFieldProps } from '../TextField/TextField';

export type InlineEditableFieldProps = TextFieldProps & {
  action: (value: unknown) => void;
  validation?: (value: unknown) => boolean;
};

const useStyles = makeStyles(() => ({
  inlineEditableField: {
    cursor: 'text',

    '& .MuiFormControl-root': {
      border: 'none',
      padding: 0,
    },

    '& .MuiInput-root input': {
      border: 'transparent',
      padding: 0,
    },
  },
  editableRegion: {
    minHeight: '23px',
    width: '100%',

    '& .fd-text': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
}));

export function InlineEditableField(
  props: InlineEditableFieldProps,
): JSX.Element {
  const {
    action,
    className = '',
    validation = null,
    value = null,
    ...rest
  } = props;

  const classes = useStyles();
  const editableField = useRef(null);
  const [fieldValue, setFieldValue] = useState(value);
  const [isEditable, setIsEditable] = useState(false);

  const toggleIsEditable = () => setIsEditable(!isEditable);

  const handleFieldAction = (): void => {
    if (!isEditable) {
      return null;
    }

    const newValue = editableField.current.value;
    if (validation && !validation(newValue)) {
      editableField.current.value = fieldValue;
      setIsEditable(true);
      return null;
    }

    action(newValue);
    setFieldValue(newValue);
    toggleIsEditable();
    return null;
  };

  const handleKeyPress = (event) => {
    const isEnterKey = event.charCode === 13;
    if (isEnterKey) {
      handleFieldAction();
    }
  };

  const inlineEditableFieldStyles = clsx(
    classes.inlineEditableField,
    className || '',
  );

  return (
    <div className={inlineEditableFieldStyles}>
      {isEditable ? (
        <ClickAwayListener onClickAway={handleFieldAction}>
          <TextField
            autoFocus
            defaultValue={fieldValue}
            InputProps={{
              disableUnderline: true,
            }}
            inputRef={editableField}
            onKeyPress={handleKeyPress}
            {...rest}
            variant="standard"
          />
        </ClickAwayListener>
      ) : (
        <div
          className={classes.editableRegion}
          onClick={toggleIsEditable}
          onKeyPress={toggleIsEditable}
          role="button"
          tabIndex={0}
        >
          <Typography>{fieldValue}</Typography>
        </div>
      )}
    </div>
  );
}
